import PropTypes from 'prop-types';
import React, { useContext, createContext, useState } from 'react';

/* ========================================================================= */
/* Context & reducer */
/* ========================================================================= */

const CursorContext = createContext();

/* ========================================================================= */
/* Hook(s) & provider(s) */
/* ========================================================================= */

/**
 * @hook useCursorContext
 * @return {object}
 *
 * Returns the cursor context object. To be used within the related provider.
 */
export const useCursorContext = () => {
    const context = useContext(CursorContext);

    if (context === undefined) {
        throw new Error('useCursor must be used within a CursorProvider');
    }

    return context;
};

/**
 * @component CursorProvider
 * @param {object} props
 *
 * A provider component setting up the context of the custom cursor.
 * 1. Defines mutation helpers to update the state of the context,
 *    each of these helpers return a cleanup function.
 * 2. Interface simulating a reducer dispatch. Returns a cleanup function.
 */
export const CursorProvider = ({ cursorMode, ...remainingProps }) => {
    const [ areas, setAreas ] = useState([]);
    const [ exclusionAreas, setExclusionAreas ] = useState([]);
    const [ mode, setMode ] = useState(cursorMode);

    const state = { areas, exclusionAreas };

    /* [1] */
    const registerArea = ({ labelContent, ref }) => {
        setAreas(_areas => _areas.concat([{ labelContent, ref }]));
        return () => setAreas(_areas => _areas.filter(area => !Object.is(area.ref, ref)));
    };

    const unsetArea = ({ ref }) => {
        setAreas(_areas => _areas.filter(area => !Object.is(area.ref, ref)));
        return () => setAreas(_areas => _areas.filter(area => !Object.is(area.ref, ref)));
    };

    const registerAreas = ({ refs }) => {
        setAreas(_areas => _areas.concat(refs.map(ref => ({ ref }))));
        return () => setAreas(_areas => _areas.filter(area => !refs.includes(area.ref)));
    };

    const registerExclusionArea = ({ ref }) => {
        setExclusionAreas(_exclusionAreas => _exclusionAreas.concat([{ ref }]));
        return () => setExclusionAreas(_exclusionAreas => _exclusionAreas.filter(area => !Object.is(area.ref, ref)));
    };

    /* [2] */
    const dispatch = ({ type, data }) => {
        switch (type) {
        case 'registerArea':
            return registerArea(data);
        case 'unsetArea':
            return unsetArea(data);
        case 'registerAreas':
            return registerAreas(data);
        case 'registerExclusionArea':
            return registerExclusionArea(data);
        default:
            throw new Error(`Unhandled action type: ${type}`);
        }
    };

    return (
        <CursorContext.Provider
            value={{ mode, setMode, state, dispatch }}
            {...remainingProps} />
    );
};

CursorProvider.propTypes = {
    cursorMode: PropTypes.oneOf(['page', 'modal'])
};
